import React, { useState } from "react";
import Box from "@mui/material/Box";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DescriptionIcon from "@mui/icons-material/Description";
import { FieldDescriptor } from "./ZodSchemaForm/types";

export interface ResultLink {
  label: string;
  url: string;
}

export interface DataSources {
  summary?: string;
  fields?: Record<string, string>;
}

export interface MutationStepData {
  id: number;
  sequence: number;
  mutationName: string;
  description: string;
  input: Record<string, any>;
  status: string;
  result?: any;
  previousState?: Record<string, any> | null;
  errorMessage?: string | null;
  fieldSchema?: FieldDescriptor[] | null;
  lockedFields?: string[] | null;
  liveSnapshot?: Record<string, any> | null;
  valueLabels?: Record<string, string> | null;
  resultLinks?: ResultLink[] | null;
  filePreviewUrl?: string | null;
  dataSources?: DataSources | null;
}

interface MutationStepRowProps {
  step: MutationStepData;
  editable: boolean;
  onInputChange?: (stepId: number, input: Record<string, any>) => void;
}

const STATUS_COLORS: Record<string, "default" | "primary" | "success" | "error" | "warning"> = {
  PENDING: "default",
  EXECUTING: "primary",
  SUCCEEDED: "success",
  FAILED: "error",
};

function formatValue(value: any, label?: string): string {
  if (value == null) return "—";
  if (label) return label;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return formatArrayValue(value);
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function formatArrayValue(arr: any[]): string {
  if (arr.length === 0) return "—";
  const first = arr[0];
  if (first?.returnYear != null && first?.returnMonth != null) {
    const last = arr[arr.length - 1];
    const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${arr.length} entries (${monthNames[first.returnMonth - 1]} ${first.returnYear} – ${monthNames[last.returnMonth - 1]} ${last.returnYear})`;
  }
  if (first?.asOfDate != null) {
    if (arr.length === 1) return `1 entry (${first.asOfDate})`;
    const last = arr[arr.length - 1];
    return `${arr.length} entries (${first.asOfDate} – ${last.asOfDate})`;
  }
  // A list of names (e.g. a result's updatedFields) reads better spelled out
  // than counted.
  if (arr.every(item => typeof item === "string")) return arr.join(", ");
  return `${arr.length} ${arr.length === 1 ? "entry" : "entries"}`;
}

function isPlainObject(value: any): boolean {
  return (
    value != null &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    !(value instanceof Date)
  );
}

/**
 * Flattens nested input objects (e.g. updateFirmFields' primaryAddress) into
 * dotted leaf paths, so each address field gets its own row instead of the
 * whole group rendering as a blob of JSON. The paths match the keys the
 * backend's resolveLabels emits ("primaryAddress.stateEnumId"), so enum
 * labels resolve for free.
 */
export function flattenFields(
  value: Record<string, any>,
  prefix = ""
): [string, any][] {
  const entries: [string, any][] = [];
  for (const [key, val] of Object.entries(value)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (isPlainObject(val)) {
      entries.push(...flattenFields(val, path));
    } else {
      entries.push([path, val]);
    }
  }
  return entries;
}

export function getAtPath(source: Record<string, any> | null, path: string): any {
  return path
    .split(".")
    .reduce<any>((acc, key) => (acc == null ? acc : acc[key]), source);
}

export function getFieldLabel(fieldName: string, fieldSchema?: FieldDescriptor[] | null): string {
  // Walk dotted paths through nested descriptors so a leaf renders as
  // "Primary Address › City" rather than the raw path.
  const parts = fieldName.split(".");
  const labels: string[] = [];
  let level: FieldDescriptor[] | null | undefined = fieldSchema;
  for (const part of parts) {
    const descriptor: FieldDescriptor | undefined = level?.find(f => f.name === part);
    labels.push(descriptor?.label ?? part);
    level = descriptor?.fields;
  }
  return labels.join(" \u203A ");
}

function getEntityDisplay(fieldName: string, fieldSchema?: FieldDescriptor[] | null): string | undefined {
  const descriptor = fieldSchema?.find(f => f.name === fieldName);
  return descriptor?.entityRef?.displayValue ?? undefined;
}

export const MutationStepRow: React.FC<MutationStepRowProps> = ({ step, editable, onInputChange }) => {
  const [expanded, setExpanded] = useState(false);
  const isStaged = step.status === "PENDING" && editable;
  const rawSnapshot = step.liveSnapshot;
  const snapshot = rawSnapshot && Object.keys(rawSnapshot).length > 0 ? rawSnapshot : null;

  const diffFields = getDiffFields(step, snapshot);

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      disableGutters
      elevation={0}
      sx={{ "&:before": { display: "none" }, border: 1, borderColor: "divider", borderRadius: 1, mb: 1 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Step {step.sequence}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {step.description || step.mutationName}
          </Typography>
          <Chip
            label={step.status}
            size="small"
            color={STATUS_COLORS[step.status] ?? "default"}
            variant="outlined"
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {isStaged && snapshot && diffFields.length > 0 ? (
          <Box>
            <Table size="small" sx={{ tableLayout: "fixed", "& td, & th": { fontSize: 12, py: 0.5, px: 1, wordBreak: "break-word", overflow: "hidden" } }}>
              <TableBody>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell sx={{ fontWeight: 600, width: "25%" }}>Field</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: "30%" }}>Current</TableCell>
                  <TableCell sx={{ width: 24 }} />
                  <TableCell sx={{ fontWeight: 600 }}>Proposed</TableCell>
                </TableRow>
                {diffFields.map(({ field, currentVal, proposedVal, changed, entityDisplay }) => {
                  const labels = step.valueLabels;
                  const currentLabel = labels?.[`${field}__current`] ?? (changed ? undefined : labels?.[field]);
                  const proposedLabel = labels?.[field];
                  return (
                    <TableRow
                      key={field}
                      sx={changed ? { bgcolor: "warning.50" } : undefined}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>
                        {getFieldLabel(field, step.fieldSchema)}
                        {entityDisplay && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            {entityDisplay}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "monospace",
                          color: changed ? "error.main" : "text.secondary",
                        }}
                      >
                        {formatValue(currentVal, currentLabel)}
                      </TableCell>
                      <TableCell sx={{ px: 0.5 }}>
                        {changed && <ArrowForwardIcon sx={{ fontSize: 12, color: "text.disabled" }} />}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "monospace",
                          color: changed ? "success.main" : "text.secondary",
                          fontWeight: changed ? 600 : 400,
                        }}
                      >
                        {formatValue(proposedVal, proposedLabel)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1.5, opacity: 0.7 }}>
              <InfoOutlinedIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption" color="text.secondary">
                To adjust these values, ask the agent in chat.
              </Typography>
            </Box>
          </Box>
        ) : isStaged && !snapshot ? (
          <Box>
            <Table size="small" sx={{ "& td, & th": { fontSize: 12, py: 0.5, px: 1 } }}>
              <TableBody>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell sx={{ fontWeight: 600, width: "30%" }}>Field</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                </TableRow>
                {flattenFields(filterHiddenFromInput(step.input, step.fieldSchema)).map(([field, value]) => {
                  const label = step.valueLabels?.[field];
                  return (
                    <TableRow key={field}>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {getFieldLabel(field, step.fieldSchema)}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace", wordBreak: "break-word" }}>
                        {formatValue(value, label)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1.5, opacity: 0.7 }}>
              <InfoOutlinedIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption" color="text.secondary">
                To adjust these values, ask the agent in chat.
              </Typography>
            </Box>
          </Box>
        ) : null}

        {/* Post-execution: show previousState-based diff */}
        {!isStaged && step.previousState && Object.keys(step.previousState).length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Changes applied:
            </Typography>
            <Table size="small" sx={{ mt: 0.5, "& td": { fontSize: 12, py: 0.5, px: 1 } }}>
              <TableBody>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell sx={{ fontWeight: 600, width: "30%" }}>Field</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Before</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>After</TableCell>
                </TableRow>
                {getAppliedFields(step).map(field => {
                  const before = getAtPath(step.previousState ?? null, field);
                  const after = getAtPath(step.input, field);
                  const changed = JSON.stringify(before) !== JSON.stringify(after);
                  const labels = step.valueLabels;
                  const beforeLabel = labels?.[`${field}__current`];
                  const afterLabel = labels?.[field];
                  return (
                    <TableRow key={field} sx={changed ? { bgcolor: "warning.50" } : undefined}>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {getFieldLabel(field, step.fieldSchema)}
                      </TableCell>
                      <TableCell sx={{ color: "error.main", fontFamily: "monospace" }}>
                        {formatValue(before, beforeLabel)}
                      </TableCell>
                      <TableCell sx={{ color: "success.main", fontFamily: "monospace" }}>
                        {formatValue(after, afterLabel)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}

        {step.dataSources && (
          <DataSourcesDisplay dataSources={step.dataSources} fieldSchema={step.fieldSchema} />
        )}

        {step.errorMessage && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {step.errorMessage}
          </Typography>
        )}

        {step.filePreviewUrl && (
          <Box sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DescriptionIcon />}
              href={step.filePreviewUrl}
              target="_blank"
              rel="noopener"
              sx={{ textTransform: "none" }}
            >
              View uploaded file
            </Button>
          </Box>
        )}

        {step.result && step.status === "SUCCEEDED" && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">Result:</Typography>
            <Table size="small" sx={{ mt: 0.5, "& td": { fontSize: 12, py: 0.25, px: 1 } }}>
              <TableBody>
                {Object.entries(step.result).map(([key, value]) => (
                  <TableRow key={key}>
                    <TableCell sx={{ fontWeight: 500, width: "35%", color: "text.secondary" }}>{key}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>{formatValue(value)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {step.resultLinks && step.resultLinks.length > 0 && (
          <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {step.resultLinks.map((link, i) => (
              <Link
                key={i}
                href={link.url}
                target="_blank"
                rel="noopener"
                variant="body2"
                data-cy="mutation-step-result-link"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
              >
                {link.label}
                <OpenInNewIcon sx={{ fontSize: 14 }} />
              </Link>
            ))}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

interface DiffField {
  field: string;
  currentVal: any;
  proposedVal: any;
  changed: boolean;
  entityDisplay?: string;
}

function filterHiddenFromInput(input: Record<string, any>, fieldSchema?: FieldDescriptor[] | null): Record<string, any> {
  if (!fieldSchema) return input;
  const hidden = new Set(fieldSchema.filter(f => f.hidden).map(f => f.name));
  if (hidden.size === 0) return input;
  const result: Record<string, any> = {};
  for (const [k, v] of Object.entries(input)) {
    if (!hidden.has(k)) result[k] = v;
  }
  return result;
}

/**
 * Fields for the post-execution "Changes applied" table: everything the step
 * snapshotted, plus any field it wrote that the snapshot could not capture
 * (updateInvestmentFields' liquidationNote is a Notes row, not an Investment
 * column), so nothing the user changed is missing from the table.
 */
function getAppliedFields(step: MutationStepData): string[] {
  const fields = flattenFields(step.previousState ?? {}).map(([field]) => field);
  const seen = new Set(fields);
  const locked = new Set(step.lockedFields ?? []);
  for (const [field] of flattenFields(
    filterHiddenFromInput(step.input, step.fieldSchema)
  )) {
    if (!seen.has(field) && !locked.has(field)) fields.push(field);
  }
  return fields;
}

function getDiffFields(step: MutationStepData, snapshot: Record<string, any> | null): DiffField[] {
  if (!snapshot) return [];

  const lockedFields = new Set(step.lockedFields ?? []);
  const hiddenFieldNames = new Set(
    (step.fieldSchema ?? []).filter(f => f.hidden).map(f => f.name)
  );
  const fields: DiffField[] = [];

  for (const [field, proposedVal] of flattenFields(step.input)) {
    if (hiddenFieldNames.has(field.split(".")[0])) continue;
    if (lockedFields.has(field)) {
      fields.push({
        field,
        currentVal: proposedVal,
        proposedVal,
        changed: false,
        entityDisplay: getEntityDisplay(field, step.fieldSchema),
      });
      continue;
    }
    const currentVal = getAtPath(snapshot, field) ?? null;
    const changed = JSON.stringify(currentVal) !== JSON.stringify(proposedVal);
    fields.push({
      field,
      currentVal,
      proposedVal,
      changed,
      entityDisplay: getEntityDisplay(field, step.fieldSchema),
    });
  }

  return fields;
}

function isSafeUrl(url: string): boolean {
  const trimmed = url.trim().toLowerCase();
  return !trimmed.startsWith("javascript:") && !trimmed.startsWith("data:");
}

function renderMarkdownLinks(text: string): React.ReactNode {
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = linkRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const href = match[2];
    if (isSafeUrl(href)) {
      parts.push(
        <Link
          key={match.index}
          href={href}
          target="_blank"
          rel="noopener"
          sx={{ fontSize: "inherit" }}
        >
          {match[1]}
        </Link>
      );
    } else {
      parts.push(match[1]);
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : text;
}

function DataSourcesDisplay({ dataSources, fieldSchema }: { dataSources: DataSources; fieldSchema?: FieldDescriptor[] | null }) {
  return (
    <Box sx={{ mt: 1, p: 1, bgcolor: "info.50", borderRadius: 1, border: 1, borderColor: "info.200" }}>
      {dataSources.summary && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          Source: {renderMarkdownLinks(dataSources.summary)}
        </Typography>
      )}
      {dataSources.fields && Object.keys(dataSources.fields).length > 0 && (
        <Box sx={{ mt: dataSources.summary ? 0.5 : 0 }}>
          {Object.entries(dataSources.fields).map(([field, citation]) => (
            <Typography key={field} variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {getFieldLabel(field, fieldSchema)}: {renderMarkdownLinks(citation)}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}
